import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import * as _ from 'underscore';

import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { AuthLevel, ServerService } from '../../../ts/services/ServerService';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { ServerUrls } from '../../ServerUrls';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { ServerSettingsSubspaceName } from '../../state/SpaceInfo';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater } from '../AnalysisVizSpace/AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater';
import { BackgroundJobsSpaceProvider } from '../BackgroundJobsSpace/BackgroundJobsSpaceProvider';
import { ConnectedReduxRouterErrorWrapper } from '../RouterHelpers/ReduxRouterErrorWrapper';
import { RouterError } from '../RouterHelpers/RouterError';
import { AllSchedulesSpaceProvider } from '../Schedules/AllSchedulesSpaceProvider';
import { ScheduleSpaceRouter } from '../Schedules/ScheduleRouter';
import { renderServerStatusSpaceSpace, ServerStatusSpaceProvider } from '../ServerStatusSpace/ServerStatusSpaceProvider';
import { ServerTasksSpaceRouter } from '../ServerTasksSpace/ServerTasksSpaceRouter';
import { ServerUserSpaceProvider } from '../ServerUser/ServerUserSpaceProvider';
import { AllServerUsersSpaceProvider } from '../ServerUsersSpace/AllServerUsersSpaceProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ServerSettingsSpaceProvider } from '../ServerSettingsSpace/ServerSettingsSpaceProvider';
import { AllSitesSpaceProvider } from './Sites/AllSitesSpaceProvider';

export interface ServerSpaceRouterOwnProps extends ReactDirectiveProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export interface ServerSpaceRouterStateProps {
  readonly urls: SpacesSiteUrls;
  readonly routerReactKey: number;
}

export type ServerSpaceRouterComponentProps = ServerSpaceRouterOwnProps & ServerSpaceRouterStateProps;

export class ServerSpaceRouterComponent extends React.PureComponent<ServerSpaceRouterComponentProps> {
  private server: ServerService;

  public constructor(props: ServerSpaceRouterComponentProps) {
    super(props);
    this.server = ServiceLocator.get<ServerService>('ServerService');
  }

  public render() {
    return (
      <div className="tb-fill">
        <Switch>
          { this.server.isUserAuthorized(AuthLevel.ServerAdmin) &&
            this.routes()
          }
          <Route render={this.error}/>
        </Switch>
      </div>
    );
  }

  private routes = () => {
    return (
      <Switch>
        <Route path={ServerUrls.getInstance().sitesPath()} render={this.serverSitesSpace} />
        {
          FeatureFlags.jobManagementEnabled &&
          <Route
            path={ServerUrls.getInstance().jobsPath()}
            render={(routeProps) => <BackgroundJobsSpaceProvider
              angularContext={this.props.angularContext}
              siteName={undefined}
              routeLocationSearch={routeProps.location.search}
              routeLocationPath={routeProps.location.pathname}
            />}
          />
        }
        <Route
          exact path={ServerUrls.getInstance().schedulesPath()}
          render={(routeProps) => <AllSchedulesSpaceProvider
            angularContext={this.props.angularContext}
            siteName={undefined}
            routeLocationSearch={routeProps.location.search}
            routeLocationPath={routeProps.location.pathname}
            urls={ServerUrls.getInstance()}
          />}
        />
        <Route
          path={ServerUrls.getInstance().schedulePath(':id')}
          render={() => <ScheduleSpaceRouter
            angularContext={this.props.angularContext}
            siteName={undefined}
            urls={ServerUrls.getInstance()}
          />}
        />
        {
        // Since the "/" is allowed in usernames historically back to early Tableau Server versions, we want to support
        // routing in these cases. However, by default, React Router doesn't handle this character in a way we want.
        // The "+" notation is a way to make the matching greedy, whereas without it we would break at all "/" characters,
        // which is the way React Router natively interprets matches.
        // See:
        // https://github.com/ReactTraining/react-router/issues/313
        // and
        // https://stackoverflow.com/questions/56133182/allow-slashes-in-route-parameter-on-react
        }
        <Route
          exact path={decodeURIComponent(ServerUrls.getInstance().userSettingsPath(':domainName', ':username+'))}
          render={(routeProps) => <ServerUserSpaceProvider
            angularContext={this.props.angularContext}
            domainName={routeProps.match.params.domainName}
            userName={routeProps.match.params.username}
          />}
        />
        <Route
          exact path={decodeURIComponent(ServerUrls.getInstance().userPath(':domainName', ':username+'))}
          render={({ match }) => <Redirect to={ServerUrls.getInstance().userSettingsPath(match.params.domainName, match.params.username)}/>}
        />
        <Route
          exact path={ServerUrls.getInstance().usersPath()}
          render={(routeProps) => <AllServerUsersSpaceProvider
            angularContext={this.props.angularContext}
            routeLocationSearch={routeProps.location.search}
            routeLocationPath={routeProps.location.pathname}
          />}
        />
        { this.getServerStatusVizRoute(this.server) }
        {
          FeatureFlags.extensionsSettingsTabEnabled &&
          <Route exact path={ServerUrls.getInstance().settingsExtensionsPath()} render={this.settingsProvider('extensions')} />
        }
        <Route exact path={ServerUrls.getInstance().settingsPath()} render={this.settingsProvider('settings')} />
        <Route exact path={ServerUrls.getInstance().settingsLicensesPath()} render={this.settingsProvider('licenses')} />
        {
          FeatureFlags.serverWelcomeChannelEnabled &&
          <Route exact path={ServerUrls.getInstance().settingsCustomizationPath()} render={this.settingsProvider('customization')} />
        }
        {
          !this.server.isSessionMultiSite() &&
          <Route exact path={ServerUrls.getInstance().addSitePath()} render={this.settingsProvider('addsite')} />
        }
        <Route
          path={ServerUrls.getInstance().statusPath()}
          render={() => <ServerStatusSpaceProvider angularContext={this.props.angularContext} renderSpace={renderServerStatusSpaceSpace} />}
        />
        <Route path={ServerUrls.getInstance().tasksPath()} render={() => <ServerTasksSpaceRouter {...this.props} />} />
        <Redirect exact from={ServerUrls.getInstance().serverPath('')} to={ServerUrls.getInstance().statusPath()} />
        <Route render={this.error}/>
      </Switch>
    );
  }

  private error = (): JSX.Element => {
    if (this.server.isUserAuthorized(AuthLevel.ServerAdmin)) {
      return <RouterError errorType={'server-default'} urls={this.props.urls}/>;
    } else {
      return <RouterError errorType={'default'} urls={this.props.urls}/>;
    }
  }

  private settingsProvider(subspaceName: ServerSettingsSubspaceName) {
    return () =>
      <ServerSettingsSpaceProvider
        subspaceName={subspaceName}
        angularContext={this.props.angularContext}
      />;
  }

  private serverSitesSpace = (props: RouteComponentProps): JSX.Element => (
    <AllSitesSpaceProvider
      angularContext={this.props.angularContext}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  )

  public static getWatchedBasePaths() {
    const serverUrls = ServerUrls.getInstance();
    return _.compact([
      serverUrls.sitesPath(),
      serverUrls.jobsPath(),
      serverUrls.schedulesPath(),
      serverUrls.baseUserPath(),
      serverUrls.serverStatusVizPath(''),
      serverUrls.settingsExtensionsPath(),
      serverUrls.settingsLicensesPath(),
      serverUrls.settingsPath(),
      serverUrls.settingsCustomizationPath(),
      serverUrls.addSitePath(),
      serverUrls.statusPath(),
      serverUrls.tasksPath()
    ]);
  }

  private getServerStatusVizRoute(server: ServerService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={ServerUrls.getInstance().serverStatusVizPath(':sheetUrlId')}
             render={(routeProps) => <AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.ConnectedUpdater
                sheetUrlId= {routeProps.match.params.sheetUrlId}
                routeLocationSearch={routeProps.location.search}
                history={routeProps.history}
                urls={this.props.urls}
                isServerAnalysis={true}
                angularContext={this.props.angularContext}
                renderSpaceProvider={AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.renderProvider}
              />}
      />;
  }
}

const mapStateToProps: MapStateToProps<ServerSpaceRouterStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  routerReactKey: state.routerSpaceReactKey
});

export const ConnectedServerSpaceRouter: React.ComponentClass<ServerSpaceRouterOwnProps>
  = connect(mapStateToProps)(ServerSpaceRouterComponent);

export interface ServerSpaceRouterProps extends ServerSpaceRouterOwnProps {}

export class ServerSpaceRouter extends React.PureComponent<ServerSpaceRouterProps> {
  public render() {
    return (
      <ConnectedReduxRouterErrorWrapper>
        <ConnectedServerSpaceRouter
          angularContext={this.props.angularContext}
          routeLocationPath={this.props.routeLocationPath}
          routeLocationSearch={this.props.routeLocationSearch}
        />
      </ConnectedReduxRouterErrorWrapper>
    );
  }
}
